import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getOverview, listCapabilities, listTools } from "@/api/endpoints";
import { Button } from "@/components/ui/Button";
import { LinkedFromCatalog } from "@/components/LinkedFromCatalog";
import { useAsync } from "@/lib/useAsync";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import type { Capability, OverviewCapability, Tool } from "@/api/types";
import { updatedLabel } from "@/lib/time";
import type { Searchable } from "@/lib/search";
import { BrowseLoading, BrowsePage } from "@/components/BrowsePage";
import { PillButton } from "@/components/ui/PillButton";
import { RegistryIntro } from "@/components/HowItFitsTogether";
import { AmbiguityIcon } from "@/components/ui/AmbiguityIcon";

/** Badge color per lifecycle status: `active` reads as settled; `deprecated` and `rejected` as a warning. */
const STATUS_TONE = {
  active: "accent",
  proposed: "neutral",
  candidate: "neutral",
  deprecated: "warn",
  rejected: "warn",
} as const;

/** One capability as the search sees it. Its text is its number, name, id, status, kinds,
 * description, aliases, inputs and outputs, scope, example requests, any ambiguity, and the
 * tools linked to it (confirmed or drafted). Its fields — what the detail page's pills
 * search — are `status`, `kind`, and its inputs and outputs: `io` (either side), `input`,
 * `output`, and `produces` (an output's type). */
function searchable(c: Capability, tools: Tool[], summary: OverviewCapability | undefined): Searchable {
  return {
    text: `${searchText(c, tools)} ${(summary?.paragraph ?? "").toLowerCase()}`,
    fields: {
      status: [c.status],
      kind: c.kinds,
      // Inputs and outputs, matched exactly (ignoring case) as the registry's own Draws from /
      // Feeds rule does: `io:` finds a term on either side, `input:`/`output:` one side, and
      // `produces:` an output type (record, determination…).
      io: [...c.inputs, ...c.outputs.map((o) => o.name)].map((t) => t.trim()),
      input: c.inputs.map((t) => t.trim()),
      output: c.outputs.map((o) => o.name.trim()),
      produces: c.outputs.map((o) => o.type).filter(Boolean) as string[],
    },
  };
}

function searchText(c: Capability, tools: Tool[]): string {
  const linked = tools.filter(
    (t) => t.supports_capabilities.includes(c.id) || t.drafted_capabilities.some((d) => d.id === c.id),
  );
  return [
    String(c.display_number),
    c.name,
    c.id,
    c.status,
    ...c.kinds,
    c.description,
    ...c.aliases,
    ...c.inputs,
    ...c.outputs.map((o) => o.name),
    ...c.scope_includes,
    ...c.scope_excludes,
    ...c.example_requests,
    c.ambiguity_note ?? "",
    ...linked.map((t) => t.name),
  ]
    .join(" ")
    .toLowerCase();
}

/** Browse the capability registry in the shared layout (components/BrowsePage.tsx): a card
 * per capability, in display-number order (the numbers are how capabilities are cited), then
 * one's detail at `/registry/<id>`. Deprecated and rejected capabilities stay listed, dimmed;
 * a question-mark icon marks an open ambiguity. Tools are loaded for each capability's
 * "Tools that support it". */
export function RegistryPage() {
  const state = useAsync(() => Promise.all([listCapabilities(), listTools(), getOverview()]), []);
  const { capabilityId } = useParams();

  if (state.status !== "ready")
    return (
      <BrowseLoading
        title="Platform Capabilities"
        intro={<RegistryIntro />}
        noun="capabilities"
        error={state.status === "error" ? state.message : undefined}
      />
    );

  const [capsData, { tools }, overview] = state.data;
  const caps = [...capsData.capabilities].sort((a, b) => a.display_number - b.display_number);
  const summaries = new Map(overview.capabilities.map((o) => [o.id, o]));
  const ambiguous = caps.filter((c) => c.ambiguity_note).length;
  const untidy = caps.filter((c) => (summaries.get(c.id)?.flags.length ?? 0) > 0).length;

  return (
    <BrowsePage
      title="Platform Capabilities"
      meta={
        <>
          <span title={updatedLabel(capsData.last_updated).title}>{updatedLabel(capsData.last_updated).text}</span>
          {" · "}
          {caps.length} capabilities
        </>
      }
      intro={<RegistryIntro />}
      items={caps}
      itemKey={(c) => c.id}
      itemPath={(c) => `/registry/${encodeURIComponent(c.id)}`}
      selectedKey={capabilityId ?? null}
      basePath="/registry"
      searchable={(c) => searchable(c, tools, summaries.get(c.id))}
      storageKey="registry"
      noun="capabilities"
      filter={{
        options: [
          { value: "", label: "Every capability" },
          { value: "ambiguity", label: `Open ambiguity (${ambiguous})` },
          { value: "untidy", label: `Metadata to tidy (${untidy})` },
        ],
        passes: (c, value) =>
          value === "ambiguity" ? Boolean(c.ambiguity_note) : (summaries.get(c.id)?.flags.length ?? 0) > 0,
      }}
      card={(c) => <CapabilityCard capability={c} />}
      row={(c) => (
        <span className="flex items-center gap-2">
          <span className="font-mono text-xs text-text/40">{c.display_number}</span>
          <span className={`flex-1 truncate ${retired(c) ? "text-text/40 line-through" : ""}`}>{c.name}</span>
          {c.ambiguity_note && <AmbiguityIcon />}
        </span>
      )}
      detail={(c, onSearch) => (
        <CapabilityDetail capability={c} summary={summaries.get(c.id)} tools={tools} onSearch={onSearch} />
      )}
    />
  );
}

const retired = (c: Capability) => c.status === "deprecated" || c.status === "rejected";

/** One capability's card: number and name, any open ambiguity, its status when it isn't
 * active, its kinds, and the start of its description. Retired ones are dimmed. */
function CapabilityCard({ capability: c }: { capability: Capability }) {
  return (
    <Card className={`h-full cursor-pointer hover:border-accent-line ${retired(c) ? "opacity-60" : ""}`}>
      <div className="mb-1 flex items-start justify-between gap-2">
        <h3 className={`font-medium ${retired(c) ? "line-through" : ""}`}>
          <span className="mr-1.5 font-mono text-xs text-text/40">{c.display_number}</span>
          {c.name}
        </h3>
        <div className="flex shrink-0 items-center gap-1">
          {c.ambiguity_note && <AmbiguityIcon />}
          {c.status !== "active" && <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>}
        </div>
      </div>
      {c.kinds.length > 0 && <p className="text-xs text-text/50">{c.kinds.join(" · ")}</p>}
      <p className="mt-2 line-clamp-4 text-sm text-text/70">{c.description}</p>
    </Card>
  );
}

/** One capability's full definition: status and kinds, description, any open ambiguity,
 * aliases, inputs/outputs, what its scope includes and excludes (an exclude is a hard veto
 * during mapping), the capabilities it draws from and feeds (shared input/output terms),
 * the neighbors it's confused with and how they differ, any metadata that reads badly in its
 * plain-language summary, the tools that support it, and its lifecycle history. The summary
 * itself sits under the description. */
function CapabilityDetail({
  capability: c,
  summary,
  tools,
  onSearch,
}: {
  capability: Capability;
  /** Its composed plain-language summary, draws-from/feeds links and metadata flags. */
  summary: OverviewCapability | undefined;
  tools: Tool[];
  onSearch: (field: string, value: string, toggle?: boolean) => void;
}) {
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-[family-name:--font-display] text-xl">{c.name}</h3>
          <p className="font-mono text-xs text-text/50">{c.id}</p>
        </div>
        <div className="flex gap-1.5">
          <PillButton
            onClick={(toggle) => onSearch("status", c.status, toggle)}
            title={`Show every ${c.status} capability`}
          >
            <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
          </PillButton>
          {c.kinds.map((k) => (
            <PillButton key={k} onClick={(toggle) => onSearch("kind", k, toggle)} title={`Show every ${k} capability`}>
              <Badge tone="accent">{k}</Badge>
            </PillButton>
          ))}
        </div>
      </div>

      <p className="leading-relaxed">{c.description}</p>

      {summary && <PlainSummary paragraph={summary.paragraph} />}

      {c.ambiguity_note && (
        <div className="flex gap-2 rounded border border-warn-line bg-warn-bg p-3 text-sm">
          <AmbiguityIcon className="mt-0.5" />
          <p>
            <strong className="text-warn">Open ambiguity.</strong> {c.ambiguity_note}
          </p>
        </div>
      )}

      <Section title="Aliases" items={c.aliases} empty="None recorded." />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Inputs</h4>
          {c.inputs.length === 0 ? (
            <p className="text-sm text-text/50">None declared.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {c.inputs.map((term) => (
                <li key={term}>
                  <IoPill term={term} onSearch={onSearch} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Outputs</h4>
          {c.outputs.length === 0 ? (
            <p className="text-sm text-text/50">None declared.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {c.outputs.map((o) => (
                <li key={o.name} className="flex items-center gap-1">
                  <IoPill term={o.name} onSearch={onSearch} />
                  {o.type && (
                    <PillButton
                      onClick={(toggle) => onSearch("produces", o.type!, toggle)}
                      title={`Show every capability producing a ${o.type}`}
                    >
                      <Badge className="border-dashed text-text/60">{o.type}</Badge>
                    </PillButton>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {summary && (summary.draws_from.length > 0 || summary.feeds.length > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <LinkedCapabilities title="Draws from" entries={summary.draws_from} />
          <LinkedCapabilities title="Feeds" entries={summary.feeds} />
        </div>
      )}

      <Section title="Scope includes" items={c.scope_includes} empty="Nothing recorded." />
      <Section title="Scope excludes (hard veto during mapping)" items={c.scope_excludes} empty="Nothing recorded." />

      {c.confused_with.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Confused with</h4>
          <ul className="flex flex-col gap-2 text-sm">
            {c.confused_with.map((cw) => (
              <li key={cw.capability}>
                <Link
                  to={`/registry/${encodeURIComponent(cw.capability)}`}
                  className="font-mono text-xs text-accent underline"
                >
                  {cw.capability}
                </Link>{" "}
                — {cw.distinction}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary && summary.flags.length > 0 && (
        <div className="rounded border border-warn-line bg-warn-bg p-3 text-sm">
          <strong className="text-warn">Metadata to tidy.</strong> These entries read badly in the plain-language
          summary:
          <ul className="mt-1 list-disc pl-5">
            {summary.flags.map((f, i) => (
              <li key={i}>
                “{f.entry}” — {f.why}
              </li>
            ))}
          </ul>
        </div>
      )}

      <LinkedFromCatalog id={c.id} link="capability" tools={tools} />

      <div>
        <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Lifecycle</h4>
        <ul className="flex flex-col gap-1 text-sm text-text/70">
          {c.lifecycle.map((e, i) => (
            <li key={i}>
              <span className="font-mono text-xs">{e.date}</span> — {e.state} ({e.by}){e.note ? ` — ${e.note}` : ""}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

/** A titled list that says so (via `empty`) when the registry records nothing, rather
 * than rendering a bare heading. */
function Section({ title, items, empty }: { title: string; items: string[]; empty: string }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">{title}</h4>
      {items.length === 0 ? (
        <p className="text-sm text-text/50">{empty}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The capability's plain-language summary — composed by the backend from its metadata
 * (domain/overview.py), never hand-written — with a Copy button, for sharing before a
 * planning session. Editing the prose means editing the metadata. */
function PlainSummary({ paragraph }: { paragraph: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded border border-line bg-sunken p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold tracking-wide text-text/60 uppercase">In plain language</h4>
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(paragraph);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            } catch {
              setCopied(false);
            }
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p className="text-sm leading-relaxed text-text/80">{paragraph}</p>
    </div>
  );
}

/** Capabilities linked by a shared input/output term, each linking to its page. */
function LinkedCapabilities({ title, entries }: { title: string; entries: { id: string; name: string }[] }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">{title}</h4>
      {entries.length === 0 ? (
        <p className="text-sm text-text/50">None.</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {entries.map((e) => (
            <li key={e.id}>
              <Link to={`/registry/${encodeURIComponent(e.id)}`} className="text-accent underline">
                {e.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** An input or output term as a pill: clicking it finds every capability that produces or
 * consumes the term (`io:`), the chain around it — the same exact match Draws from / Feeds use. */
function IoPill({
  term,
  onSearch,
}: {
  term: string;
  onSearch: (field: string, value: string, toggle?: boolean) => void;
}) {
  return (
    <PillButton
      onClick={(toggle) => onSearch("io", term.trim(), toggle)}
      title={`Show every capability that produces or uses "${term}"`}
    >
      <Badge tone="brand">{term}</Badge>
    </PillButton>
  );
}
